import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {Form} from "react-final-form";
import moment from "moment";
import {format} from "date-fns";
import DateTimeField from "../../../FinalForm/DatePickerField";
import {FiRefreshCcw, FiMapPin} from "react-icons/fi";
import {MdFaceUnlock} from "react-icons/md";
import {DefaultLoader} from "../../../components/DefaultLoader";
import {FilterButton} from "../components/Button";
import PageHeader from "../components/PageHeader";
import {apiGetFaceRecognitionStats} from "./Actions";

// ─── styles ───────────────────────────────────────────────────────────────────

const cardStyle = {
    border:"1px solid #e2e8f0",borderRadius:"10px",background:"#fff",
    padding:"14px",display:"flex",flexDirection:"column",gap:"8px"
};
const titleStyle = {
    fontSize: "var(--enigoo-fs-md)",fontWeight:700,color:"#1e293b",
    borderBottom:"1px solid #f1f5f9",paddingBottom:"6px"
};
const metricGridStyle = {display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(210px,1fr))",gap:"10px"};

// karta ve stylu "Stav služeb" na /turnstile-dashboard
const MetricCard = ({label, desc, value, color, bg, border, dot = true}) => (
    <div style={{
        border: `1px solid ${border}`,
        borderLeft: `4px solid ${color}`,
        borderRadius:"10px", background: bg, padding:"14px",
        display:"flex", flexDirection:"column", gap:"6px",
    }}>
        <span style={{fontSize: "var(--enigoo-fs-xs)",fontWeight:600,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.05em"}}>{label}</span>
        <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
            {dot && <span style={{
                width:"10px", height:"10px", borderRadius:"50%", flexShrink:0,
                background: color, boxShadow: `0 0 6px ${color}`,
            }}/>}
            <span style={{fontSize: "var(--enigoo-fs-3xl)",fontWeight:700,color,lineHeight:1}}>{value ?? "—"}</span>
        </div>
        {desc && <span style={{fontSize: "var(--enigoo-fs-2xs)",color:"#94a3b8",lineHeight:1.3}}>{desc}</span>}
    </div>
);

const rateColor = (rate) => {
    if (rate === null || rate === undefined) return "#64748b";
    if (rate >= 80) return "#16a34a";
    if (rate >= 50) return "#d97706";
    return "#dc2626";
};

// ─── quick-select presets ─────────────────────────────────────────────────────

const getPresets = () => {
    const now = moment();
    return [
        {
            label: "Posledních 10 minut",
            dateFrom: now.clone().subtract(10, "minutes").format("YYYY-MM-DD HH:mm:ss"),
            dateTo:   now.format("YYYY-MM-DD HH:mm:ss"),
        },
        {
            label: "Posledních 30 minut",
            dateFrom: now.clone().subtract(30, "minutes").format("YYYY-MM-DD HH:mm:ss"),
            dateTo:   now.format("YYYY-MM-DD HH:mm:ss"),
        },
        {
            label: "Poslední hodinu",
            dateFrom: now.clone().subtract(1, "hour").format("YYYY-MM-DD HH:mm:ss"),
            dateTo:   now.format("YYYY-MM-DD HH:mm:ss"),
        },
        {
            label: "Dnes",
            dateFrom: now.clone().startOf("day").format("YYYY-MM-DD HH:mm:ss"),
            dateTo:   now.clone().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
        },
        {
            label: "Včera",
            dateFrom: now.clone().subtract(1, "day").startOf("day").format("YYYY-MM-DD HH:mm:ss"),
            dateTo:   now.clone().subtract(1, "day").endOf("day").format("YYYY-MM-DD HH:mm:ss"),
        },
        {
            label: "Posledních 7 dní",
            dateFrom: now.clone().subtract(6, "days").startOf("day").format("YYYY-MM-DD HH:mm:ss"),
            dateTo:   now.clone().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
        },
        {
            label: "Tento měsíc",
            dateFrom: now.clone().startOf("month").format("YYYY-MM-DD HH:mm:ss"),
            dateTo:   now.clone().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
        },
    ];
};

// ─── global summary card ──────────────────────────────────────────────────────

const GlobalCard = ({data}) => (
    <div style={cardStyle}>
        <div style={titleStyle}>Celkový přehled</div>
        <div style={metricGridStyle}>
            <MetricCard
                label="Získání obrázku"
                desc="Čtečka si získala obrázek pro porovnání."
                value={data.imageGetSuccess}
                color="#2563eb" bg="#eff6ff" border="#bfdbfe"/>
            <MetricCard
                label="Obličej nerozpoznán"
                desc="Získaný obrázek čtečka vyhodnotila jako nepodobný aktuální podobě osoby."
                value={data.faceNotRecognized}
                color="#dc2626" bg="#fff5f5" border="#fecaca"/>
            <MetricCard
                label="Úspěšný vchod"
                desc="Získaný obrázek čtečka vyhodnotila jako stejnou osobu."
                value={data.entrySuccess}
                color="#16a34a" bg="#f0fdf4" border="#bbf7d0"/>
            <MetricCard
                label="Celkem záznamů"
                value={data.total}
                color="#475569" bg="#f8fafc" border="#e2e8f0" dot={false}/>
            <MetricCard
                label="Úspěšnost rozpoznávání"
                value={data.recognitionRate !== null && data.recognitionRate !== undefined ? `${data.recognitionRate} %` : "—"}
                color={rateColor(data.recognitionRate)} bg="#fff" border="#e2e8f0" dot={false}/>
        </div>
    </div>
);

// ─── per-turnstile table ──────────────────────────────────────────────────────

const thStyle = {
    padding:"8px 10px", textAlign:"left", fontSize: "var(--enigoo-fs-xs)", fontWeight:700,
    color:"#475569", borderBottom:"2px solid #e2e8f0", background:"#f8fafc"
};
const tdStyle = {padding:"8px 10px", fontSize: "var(--enigoo-fs-sm)", borderBottom:"1px solid #f1f5f9"};

const TurnstileTable = ({turnstiles}) => (
    <div style={cardStyle}>
        <div style={titleStyle}>Přehled podle čteček</div>
        {turnstiles.length === 0
            ? <div style={{fontSize: "var(--enigoo-fs-sm)",color:"#94a3b8",textAlign:"center",padding:"12px"}}>Žádná data</div>
            : (
                <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse"}}>
                        <thead>
                            <tr>
                                <th style={thStyle}>Čtečka</th>
                                <th style={{...thStyle,textAlign:"right",color:"#2563eb"}}>Získání obrázku</th>
                                <th style={{...thStyle,textAlign:"right",color:"#dc2626"}}>Obličej nerozpoznán</th>
                                <th style={{...thStyle,textAlign:"right",color:"#16a34a"}}>Úspěšný vchod</th>
                                <th style={{...thStyle,textAlign:"right"}}>Celkem</th>
                                <th style={{...thStyle,textAlign:"right"}}>Úspěšnost</th>
                            </tr>
                        </thead>
                        <tbody>
                            {turnstiles.map(t => (
                                <tr key={t.turnstileId}>
                                    <td style={tdStyle}>
                                        <div style={{display:"flex",alignItems:"center",gap:"6px"}}>
                                            <MdFaceUnlock size={14} color="#475569"/>
                                            <span style={{fontWeight:600}}>{t.turnstileName}</span>
                                            <span style={{fontSize: "var(--enigoo-fs-2xs)",color:"#94a3b8"}}>#{t.turnstileId}</span>
                                        </div>
                                    </td>
                                    <td style={{...tdStyle,textAlign:"right",color:"#2563eb",fontWeight:700}}>{t.imageGetSuccess}</td>
                                    <td style={{...tdStyle,textAlign:"right",color:"#dc2626",fontWeight:700}}>{t.faceNotRecognized}</td>
                                    <td style={{...tdStyle,textAlign:"right",color:"#16a34a",fontWeight:700}}>{t.entrySuccess}</td>
                                    <td style={{...tdStyle,textAlign:"right",fontWeight:600}}>{t.total}</td>
                                    <td style={{...tdStyle,textAlign:"right",fontWeight:700,color:rateColor(t.recognitionRate)}}>
                                        {t.recognitionRate !== null && t.recognitionRate !== undefined
                                            ? `${t.recognitionRate} %`
                                            : "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )
        }
    </div>
);

// ─── per-zone table ───────────────────────────────────────────────────────────

const ZoneTable = ({zones}) => (
    <div style={cardStyle}>
        <div style={titleStyle}>Přehled podle zón</div>
        {zones.length === 0
            ? <div style={{fontSize: "var(--enigoo-fs-sm)",color:"#94a3b8",textAlign:"center",padding:"12px"}}>Žádná data</div>
            : (
                <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse"}}>
                        <thead>
                            <tr>
                                <th style={thStyle}>Zóna</th>
                                <th style={{...thStyle,textAlign:"right",color:"#2563eb"}}>Získání obrázku</th>
                                <th style={{...thStyle,textAlign:"right",color:"#dc2626"}}>Obličej nerozpoznán</th>
                                <th style={{...thStyle,textAlign:"right",color:"#16a34a"}}>Úspěšný vchod</th>
                                <th style={{...thStyle,textAlign:"right"}}>Celkem</th>
                                <th style={{...thStyle,textAlign:"right"}}>Úspěšnost</th>
                            </tr>
                        </thead>
                        <tbody>
                            {zones.map(z => (
                                <tr key={z.zoneId ?? "none"}>
                                    <td style={tdStyle}>
                                        <div style={{display:"flex",alignItems:"center",gap:"6px"}}>
                                            <FiMapPin size={14} color="#475569"/>
                                            <span style={{fontWeight:600}}>{z.zoneName}</span>
                                            {z.zoneId != null && <span style={{fontSize: "var(--enigoo-fs-2xs)",color:"#94a3b8"}}>#{z.zoneId}</span>}
                                        </div>
                                    </td>
                                    <td style={{...tdStyle,textAlign:"right",color:"#2563eb",fontWeight:700}}>{z.imageGetSuccess}</td>
                                    <td style={{...tdStyle,textAlign:"right",color:"#dc2626",fontWeight:700}}>{z.faceNotRecognized}</td>
                                    <td style={{...tdStyle,textAlign:"right",color:"#16a34a",fontWeight:700}}>{z.entrySuccess}</td>
                                    <td style={{...tdStyle,textAlign:"right",fontWeight:600}}>{z.total}</td>
                                    <td style={{...tdStyle,textAlign:"right",fontWeight:700,color:rateColor(z.recognitionRate)}}>
                                        {z.recognitionRate !== null && z.recognitionRate !== undefined
                                            ? `${z.recognitionRate} %`
                                            : "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )
        }
    </div>
);

// ─── main ─────────────────────────────────────────────────────────────────────

const FaceRecognitionStats = () => {
    const [data, setData]             = useState(null);
    const [loading, setLoading]       = useState(false);
    const [activePreset, setActivePreset] = useState("Dnes");
    const user = useSelector(s => s.user);

    const today = format(new Date(), "yyyy-MM-dd");

    useEffect(() => {
        if (user?.activeAreal?.id) {
            const dnes = getPresets().find(p => p.label === "Dnes");
            loadData(dnes.dateFrom, dnes.dateTo);
        }
    }, [user.activeAreal]);

    const loadData = (dateFrom, dateTo) => {
        setLoading(true);
        apiGetFaceRecognitionStats(user.activeAreal.id, dateFrom, dateTo, (response) => {
            setData(response);
            setLoading(false);
        }, () => {
            setData(null);
            setLoading(false);
        });
    };

    const onPreset = (preset) => {
        setActivePreset(preset.label);
        loadData(preset.dateFrom, preset.dateTo);
    };

    // hodnota z DateTimeField je "YYYY-MM-DDTHH:mm" (čas zadán) nebo "YYYY-MM-DD" (jen datum)
    const toApiDateTime = (value, fallbackTime) => {
        if (!value) return null;
        if (value.includes("T")) {
            const withSeconds = value.split(":").length === 2 ? `${value}:00` : value;
            return withSeconds.replace("T", " ");
        }
        return `${value} ${fallbackTime}`;
    };

    const onSubmit = (values) => {
        setActivePreset(null);
        const from = toApiDateTime(values.dateFrom, "00:00:00") || `${today} 00:00:00`;
        const to   = toApiDateTime(values.dateTo, "23:59:59") || moment().format("YYYY-MM-DD HH:mm:ss");
        loadData(from, to);
    };

    const presetBtnStyle = (label) => ({
        padding:"5px 12px", borderRadius:"6px", fontSize: "var(--enigoo-fs-xs)", fontWeight:600,
        cursor:"pointer", border:"1px solid",
        background: activePreset === label ? "#2563eb" : "#f8fafc",
        color:      activePreset === label ? "#fff"    : "#475569",
        borderColor:activePreset === label ? "#2563eb" : "#e2e8f0",
    });

    return (
        <>
            <PageHeader crumbs={[{label: "Statistiky rozpoznávání obličejů"}]}/>

            <div style={{border:"1px solid #e2e8f0",borderRadius:"10px",background:"#fff",marginBottom:"14px",padding:"12px"}}>
                {/* quick-select */}
                <div style={{display:"flex",flexWrap:"wrap",gap:"6px",marginBottom:"12px"}}>
                    {getPresets().map(p => (
                        <button key={p.label} style={presetBtnStyle(p.label)} onClick={() => onPreset(p)}>
                            {p.label}
                        </button>
                    ))}
                </div>

                {/* custom range */}
                <Form
                    initialValues={{dateFrom: today}}
                    onSubmit={onSubmit}
                    render={({handleSubmit, values}) => (
                        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:"8px"}}>
                            <DateTimeField name={"dateFrom"} isMandatory={true} valueToCompare={values.dateTo}
                                validateFun={(d1, d2) => moment(d1).isAfter(moment(d2)) ? "Datum od nemůže být po datu do" : ""}
                                label={"Datum od"} type={"from"} placeholder={"Datum od"} showTimeSelect={true} onEnter={handleSubmit}/>
                            <DateTimeField name={"dateTo"} isMandatory={false} valueToCompare={values.dateFrom}
                                validateFun={(d1, d2) => moment(d2).isAfter(moment(d1)) ? "Datum od nemůže být po datu do" : ""}
                                label={"Datum do"} type={"to"} placeholder={"Datum do (volitelné)"} showTimeSelect={true} onEnter={handleSubmit}/>
                            <div style={{display:"flex",alignItems:"flex-end",justifyContent:"flex-end",gridColumn:"1 / -1"}}>
                                <FilterButton onClick={handleSubmit} disabled={loading} text="Načíst vlastní rozsah" icon={<FiRefreshCcw size={12}/>}/>
                            </div>
                        </div>
                    )}
                />
            </div>

            {loading
                ? <DefaultLoader text={"Načítám statistiky..."}/>
                : data && (
                    <div style={{display:"flex",flexDirection:"column",gap:"12px"}}>
                        <GlobalCard data={data}/>
                        <ZoneTable zones={data.zones || []}/>
                        <TurnstileTable turnstiles={data.turnstiles || []}/>
                    </div>
                )
            }
        </>
    );
};

export default FaceRecognitionStats;
